import useAppInit from "@/router/useAppInit.js";
import useRedirect from "@/router/useRedirect.js";
import { isUsingProductionData } from "@/util/hostnames.ts";
import { useStore } from "vuex";
import useViewData from "@/router/useViewData.js";

function addPersistentQuery(to, from) {
  if (from.fullpath === "/") {
    // first page load so continue
    return false;
  }
  // params that must be persisted
  const persistentParams = ["filter"];
  const missingParams = [];
  Object.keys(from.query).forEach((key) => {
    if (persistentParams.includes(key) && to.query[key] === undefined) {
      missingParams.push(key);
    }
  });
  if (missingParams.length === 0) {
    // no missing params so continue
    return false;
  }
  const updated = { path: to.path, query: { ...to.query }, params: to.params };
  missingParams.forEach((key) => {
    updated.query[key] = from.query[key];
  });
  return updated;
}

async function beforeEach(to, from) {
  const store = useStore();
  const { appInit } = useAppInit();
  const { preDataRedirect, postDataRedirect, redirectHashUrls } = useRedirect();
  const { loadViewData } = useViewData();

  // prevent navigation when data is saving by returning false
  if (store.getters.getIsSaving) {
    return false;
  }

  // redirect # urls that have been registered as 404 to the correct location
  if (to.hash) {
    return redirectHashUrls(to);
  }

  // If the page has persistent URL queries,
  // ensure they're maintained
  const withPersistentRouteQuery = addPersistentQuery(to, from);
  if (withPersistentRouteQuery) {
    return withPersistentRouteQuery;
  }

  // Pages that don't need data or initialization
  const noInitPages = [
    "Login",
    "Redirect",
    "CastScreen",
    "NotFound404",
    "LinkAccounts",
    "LinkAccountsContinue",
  ];

  if (noInitPages.includes(to.name)) {
    return undefined;
  }

  store.commit("setRoutePending", true);

  try {
    // 1. One-time initialization
    if (!store.getters.getStyleID) {
      await appInit(to);
    }

    // 2. Check for a redirect
    let redirect = preDataRedirect(to);
    if (redirect !== undefined) {
      return redirect;
    }

    // 3. Load view data
    await loadViewData(to);

    // 4. Check again for a redirect
    redirect = postDataRedirect(to);
    if (redirect !== undefined) {
      return redirect;
    }
  } catch (error) {
    console.error(error);
    const errorMessage = error?.response?.data?.error;

    if (to.name === "PostRedirect") {
      return undefined;
    }

    // if 404 then redirect
    if (error?.response?.status === 404) {
      if (import.meta.env.DEV) {
        return { name: "NotFound404" };
      }
      if (
        isUsingProductionData() &&
        errorMessage ===
          `No document exists for siteID ${store.getters.getStyleID}`
      ) {
        // if the siteID is not found, redirect to marketing site (this excludes custom domains)
        // (unless we're in touchscreen mode, in that case, just show an error modal)
        if (store.getters.getTouchScreen) {
          store.dispatch("setServerErrorMessage", {
            message: errorMessage,
            returnToPreviousPage: false,
          });
        } else {
          window.location = "https://rocketalumnisolutions.com";
          return undefined;
        }
      } else {
        return {
          name: "Home",
          params: { styleid: store.getters.getStyleID },
        };
      }
    }

    // otherwise send 400s to not found page and all other errors (500s) to error page
    if (error?.response?.status < 500) {
      // setSiteId to be blank so that the appInit is called again after redirect to default route
      // otherwise the site will try loadData again with clientID=0 and reach infinite loop
      await store.dispatch("api/setSiteId", "");
      return { name: "NotFound404" };
    }
    let serverErrorMessage =
      "Server Error. Close popup to refresh and try again.";
    // backend returns error messages in different formats
    if (typeof error.response?.data === "object" && error.response.data.error) {
      serverErrorMessage = error.response.data.error;
    } else if (error.response?.data) {
      serverErrorMessage = error.response.data;
    }
    await store.dispatch("setServerErrorMessage", {
      message: serverErrorMessage,
      returnToPreviousPage: true,
    });
  }
  return undefined;
}

export { beforeEach };
